import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { StyledModal } from 'components/shared';
import { Copy } from 'components/components/atoms';
import CloseIcon from 'components/icons/Close';
import breakpoints from 'utils/breakpoints';

const ModalContent = styled.div`
  width: 89.5vw;
  max-height: 50vh;
  max-height: 50dvh;
  margin: auto;

  @media ${breakpoints.md} {
    width: 65vw;
    max-height: 65vh;
    max-height: 65dvh;
    max-width: 59.625rem;
  }
`;

// -webkit-touch-callout is only supported by iOS WebKit (Safari and Chrome on iOS).
const iOSOnly = '@supports (-webkit-touch-callout: none)';

// Custom scrollbar for mobile (iOS/Android hide overlay scrollbars until scrolls).
// iOS ignores ::-webkit-scrollbar styles, so ScrollTrack is drawn instead.
const ScrollBox = styled.div`
  -webkit-overflow-scrolling: auto;

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #8e8e8e;
    border-radius: 3px;
  }

  @supports not selector(::-webkit-scrollbar) {
    scrollbar-width: thin;
    scrollbar-color: #8e8e8e transparent;
  }

  ${iOSOnly} {
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ScrollTrack = styled.div`
  display: none;
  position: absolute;
  top: 6px;
  bottom: 6px;
  right: 4px;
  width: 6px;
  pointer-events: none;

  ${iOSOnly} {
    display: block;
  }
`;

const ScrollThumb = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  border-radius: 3px;
  background-color: #8e8e8e;
`;

const PromoDisclaimerModal = ({
  isOpen = false,
  disclaimerText,
  modalRef,
  onClose = () => {},
}) => {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);

  const updateThumb = () => {
    const box = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!box || !track || !thumb) return;

    const { scrollTop, scrollHeight, clientHeight } = box;
    const maxScroll = scrollHeight - clientHeight;
    if (maxScroll <= 0) {
      track.style.visibility = 'hidden';
      return;
    }

    const trackHeight = track.clientHeight;
    const thumbHeight = Math.max(
      (clientHeight / scrollHeight) * trackHeight,
      24,
    );
    const thumbTop =
      (Math.min(Math.max(scrollTop, 0), maxScroll) / maxScroll) *
      (trackHeight - thumbHeight);

    track.style.visibility = 'visible';
    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  };

  const scrollToTop = () => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    updateThumb();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const timers = [0, 150, 400].map((ms) => setTimeout(scrollToTop, ms));

    let observer;
    const observeTimer = setTimeout(() => {
      const box = scrollRef.current;
      if (!box || typeof ResizeObserver === 'undefined') return;
      observer = new ResizeObserver(updateThumb);
      observer.observe(box);
      if (box.firstElementChild) observer.observe(box.firstElementChild);
    }, 0);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(observeTimer);
      if (observer) observer.disconnect();
    };
  }, [isOpen]);

  return (
    <StyledModal
      isOpen={isOpen}
      onAfterOpen={scrollToTop}
      className='select-none flex w-full'
      data-aos='fade'
    >
      <ModalContent
        className='flex flex-col overflow-hidden rounded'
        aria-modal='true'
        role='dialog'
      >
        <div
          tabIndex={0}
          ref={modalRef}
          className='w-full flex-1 min-h-0 flex flex-col bg-white rounded text-action-blue pt-10 pb-8 px-5 md:px-10 md:pb-10 relative'
          onKeyDown={(e) => {
            if (e.shiftKey && e.key === 'Tab') {
              onClose();
            }
          }}
        >
          <button
            className='flex justify-end mt-3 mr-3 z-10 text-mattamy-blue absolute right-0 top-0'
            onClick={onClose}
            aria-label='Close disclaimer text modal'
          >
            <CloseIcon className='w-8' />
          </button>

          <div className='font-bold font-graphie text-mattamy-blue text-lg md:text-2xl leading-tight text-left pr-10 mb-4'>
            Terms and Conditions
          </div>

          <div className='relative flex-1 min-h-0 flex flex-col border border-mattamy-gray-light'>
            <ScrollBox
              ref={scrollRef}
              onScroll={updateThumb}
              className='flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-3 md:px-6 md:py-4'
            >
              <Copy
                onKeyDown={(e) => {
                  if (e.key === 'Tab') {
                    modalRef?.current?.focus({ preventScroll: true });
                  }
                }}
                className='text-left tracking-tight'
              >
                {disclaimerText}
              </Copy>
            </ScrollBox>

            <ScrollTrack ref={trackRef} aria-hidden='true'>
              <ScrollThumb ref={thumbRef} />
            </ScrollTrack>
          </div>
        </div>
      </ModalContent>
    </StyledModal>
  );
};

PromoDisclaimerModal.propTypes = {
  isOpen: PropTypes.bool,
  disclaimerText: PropTypes.string,
  modalRef: PropTypes.shape({
    current: PropTypes.instanceOf(
      typeof Element === 'undefined' ? Object : Element,
    ),
  }),
  onClose: PropTypes.func,
};

export default PromoDisclaimerModal;
